import { motion, type MotionProps } from 'motion/react';
import type { ElementType } from 'react';

import { cn } from '~/lib/utils';

interface AnimatedWordsProps {
  text: string;
  isInView: boolean;
  as?: ElementType;
  className?: string;
  wordClassName?: string;
  initial: MotionProps['initial'];
  animate: MotionProps['animate'];
  transition: MotionProps['transition'];
  delayStep?: number;
}

const buildTransition = (
  transition: MotionProps['transition'],
  delay: number
): MotionProps['transition'] => {
  if (!transition || typeof transition !== 'object') {
    return { delay };
  }

  const base: Record<
    string,
    unknown
  > = { ...(transition as Record<string, unknown>), delay };

  const opacity = (transition as Record<string, unknown>).opacity;
  if (opacity && typeof opacity === 'object') {
    base.opacity = { ...(opacity as Record<string, unknown>), delay };
  }

  const y = (transition as Record<string, unknown>).y;
  if (y && typeof y === 'object') {
    base.y = { ...(y as Record<string, unknown>), delay };
  }

  return base as MotionProps['transition'];
};

export const AnimatedWords = ({
  text,
  isInView,
  as,
  className,
  wordClassName,
  initial,
  animate,
  transition,
  delayStep = 0,
}: AnimatedWordsProps) => {
  const Comp = (as ?? 'span') as ElementType;
  const words = text.split(' ');

  return (
    <Comp className={className}>
      {words.map((word, i) => (
        <span key={`${word}-${i}`} className="inline-block align-bottom">
          <motion.span
            className={cn('inline-block', wordClassName)}
            initial={initial}
            animate={isInView ? animate : initial}
            transition={buildTransition(transition, i * delayStep)}
          >
            {word}
          </motion.span>
          {i < words.length - 1 && '\u00A0'}
        </span>
      ))}
    </Comp>
  );
};
